import { Box, Card, CardContent, IconButton, Typography } from "@mui/material";
import { Trash2 } from "lucide-react";
import { Component, type ReactNode } from "react";
import { WIDGET_REGISTRY, type Widget } from "./registry";

class WidgetErrorBoundary extends Component<
  { title: string | null; children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.warn("WidgetCard render failed:", error);
  }
  render() {
    if (this.state.error) {
      return (
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent>
            {this.props.title && (
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {this.props.title}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Could not render this widget: {this.state.error.message}
            </Typography>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

export function WidgetCard({
  widget,
  onDelete,
}: {
  widget: Widget;
  onDelete?: (id: string) => void;
}) {
  const Renderer = WIDGET_REGISTRY[widget.kind];
  if (!Renderer) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Unknown widget kind: {widget.kind}
          </Typography>
        </CardContent>
      </Card>
    );
  }
  return (
    <WidgetErrorBoundary title={widget.title}>
      <Box
        data-ph-scope="widget"
        sx={{
          position: "relative",
          height: "100%",
          // Wrap each renderer in a consistent modern shell so per-kind
          // cards inherit identical borders / shadows / hover affordances.
          "& > .MuiCard-root": {
            height: widget.kind === "kpi" ? "auto" : "100%",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            boxShadow:
              "0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02)",
            backgroundImage:
              "linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 60%)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                "0 8px 24px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.04)",
            },
          },
          "&:hover .widget-delete": { opacity: 1 },
          "@media (hover: none)": { ".widget-delete": { opacity: 0.85 } },
        }}
      >
        <Renderer widget={widget} />
        {onDelete && (
          <IconButton
            className="widget-delete"
            aria-label="delete widget"
            data-ph="delete"
            size="small"
            onClick={() => onDelete(widget.id)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              opacity: { xs: 0.85, md: 0 },
              transition: "opacity 0.15s ease",
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              "&:hover": { opacity: 1, color: "error.main", bgcolor: "background.paper" },
            }}
          >
            <Trash2 size={14} />
          </IconButton>
        )}
      </Box>
    </WidgetErrorBoundary>
  );
}

export type { Widget } from "./registry";
